import { parse } from "yaml";
import type { AdminPage, PageMetadata } from "./types";
import { normalizeComponents } from "./pageEditorUtils";

/** Single # title (not ##). Must have space after # so ## Section is not matched. */
const H1_REGEX = /^#\s+(.+)$/;

/** Minimal frontmatter reader for the fixed key set slug/language/title/description. Values may
 *  be quoted or bare; on any parse hiccup we fall back to stripping quotes by hand rather than
 *  losing the value. Anything outside the --- fences is returned untouched as `body`. */
function parseFrontmatter(str: string): { slug: string; language: string; title: string; description: string; body: string } {
	const trimmed = str.trim();
	if (!trimmed.startsWith("---")) {
		return { slug: "", language: "fr", title: "", description: "", body: trimmed };
	}
	const end = trimmed.indexOf("---", 3);
	if (end < 0) return { slug: "", language: "fr", title: "", description: "", body: trimmed };
	const block = trimmed.slice(3, end).trim();
	const body = trimmed.slice(end + 3).trim();
	let slug = "";
	let language = "fr";
	let title = "";
	let description = "";
	for (const line of block.split("\n")) {
		const colon = line.indexOf(":");
		if (colon < 0) continue;
		const k = line.slice(0, colon).trim();
		const v = line.slice(colon + 1).trim();
		try {
			const parsed = v.startsWith('"') || v.startsWith("'") ? JSON.parse(v.replace(/'/g, '"')) : v;
			if (k === "slug") slug = String(parsed ?? "");
			else if (k === "language") language = String(parsed ?? "fr");
			else if (k === "title") title = String(parsed ?? "").trim();
			else if (k === "description") description = String(parsed ?? "").trim();
		} catch {
			if (k === "slug") slug = v.replace(/^["']|["']$/g, "");
			else if (k === "language") language = v.replace(/^["']|["']$/g, "") || "fr";
			else if (k === "title") title = v.replace(/^["']|["']$/g, "").trim();
			else if (k === "description") description = v.replace(/^["']|["']$/g, "").trim();
		}
	}
	return { slug, language, title, description, body };
}

const SECTION_HEADING_REGEX = /^##\s+(.+)$/;
const BLOCK_OPEN_REGEX = /^:::\s*(\w+)\s*$/;
const BLOCK_CLOSE_REGEX = /^:::\s*$/;

/** Fenced code block: ```ArticleHero (component type as "language"). */
const FENCED_ARTICLE_HERO_OPEN_REGEX = /^```\s*ArticleHero\s*$/i;
const FENCED_BLOCK_CLOSE_REGEX = /^```\s*$/;

/** Remove unusual line terminators (LS U+2028, PS U+2029) so the editor does not warn. */
function normalizeLineTerminators(s: string): string {
	return s.replace(/\u2028/g, "\n").replace(/\u2029/g, "\n");
}

/** Parse `image: src | alt` shorthand into featuredImage. Returns [restYaml, featuredImage] or null if no image. */
function parseImageShorthand(value: string): { src: string; alt: string } | null {
	const trimmed = value.trim();
	if (!trimmed) return null;
	const pipeIdx = trimmed.indexOf("|");
	if (pipeIdx >= 0) {
		const src = trimmed.slice(0, pipeIdx).trim();
		const alt = trimmed.slice(pipeIdx + 1).trim() || "Image";
		return src ? { src, alt } : null;
	}
	return { src: trimmed, alt: "Image" };
}

/**
 * Preprocess YAML so unquoted values containing ": " are quoted.
 * YAML treats ": " as key-value separator, so "title: A : B" fails. Quoting fixes it.
 */
function preprocessArticleHeroYaml(yamlStr: string): string {
	return yamlStr
		.split(/\r?\n/)
		.map((line) => {
			// Only top-level key: value (no leading indent)
			const match = line.match(/^([a-zA-Z0-9_]+):\s*(.*)$/);
			if (!match) return line;
			const key = match[1];
			const value = match[2];
			if (!value || value.startsWith('"') || value.startsWith("'")) return line;
			if (!value.includes(": ")) return line;
			// Value contains ": " - wrap in double quotes and escape internal "
			const escaped = value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
			return `${key}: "${escaped}"`;
		})
		.join("\n");
}

/** Parse ArticleHero fenced block or ::: block content. Handles image shorthand and strips shareLabel/shareOptions. */
function parseArticleHeroBlock(yamlStr: string): Record<string, unknown> {
	if (!yamlStr.trim()) return {};
	const preprocessed = preprocessArticleHeroYaml(yamlStr);
	let parsed: unknown;
	try {
		parsed = parse(preprocessed);
	} catch {
		return {};
	}
	if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return {};
	return parseArticleHeroContent(parsed as Record<string, unknown>);
}

function parseArticleHeroContent(obj: Record<string, unknown>): Record<string, unknown> {
	const { shareLabel: _sl, shareOptions: _so, image: imageShorthand, ...rest } = obj;
	let finalContent: Record<string, unknown> = rest;

	// image: src | alt → featuredImage: { src, alt }
	if (imageShorthand != null && typeof imageShorthand === "string") {
		const parsed = parseImageShorthand(imageShorthand);
		if (parsed) finalContent.featuredImage = parsed;
	}
	// If featuredImage already present (from full YAML), keep it
	if (!finalContent.featuredImage && rest.featuredImage && typeof rest.featuredImage === "object") {
		finalContent.featuredImage = rest.featuredImage;
	}

	return finalContent;
}

/**
 * Inverse of pageToEditableMarkdown: parses the editable Markdown dialect back into an AdminPage.
 * The dialect is line-based : `# Title` → PageHeader, `## Text` → SectionHeading,
 * `:::ComponentType` blocks with a YAML body → arbitrary components, a fenced ```ArticleHero
 * block → ArticleHero, and any remaining free text becomes TextBlock paragraphs (blank-line
 * separated, markdown-style).
 *
 * Two properties matter more than parsing elegance here:
 * - Forgiveness: this runs on every debounced keystroke in markdown mode, so an unparseable YAML
 *   body yields an empty component instead of throwing : a half-typed block must never blow away
 *   the rest of the page.
 * - Losslessness for what the dialect can't express: taxonomies, layout, dynamic slugs etc. ride
 *   along from `base` untouched; frontmatter only overrides slug/language/title/description when
 *   present. That is what makes the blocks → markdown → blocks round-trip safe.
 */
export function editableMarkdownToPage(md: string, base: AdminPage | null, fallbackLanguage: string): AdminPage {
	const normalizedMd = normalizeLineTerminators(md);
	const baseFallback: AdminPage = base ?? {
		slug: "",
		language: fallbackLanguage,
		title: "",
		metadata: {},
		components: []
	};

	const { slug, language, title: frontmatterTitle, description: frontmatterDescription, body: rawBody } = parseFrontmatter(normalizedMd);

	const components: Array<{ componentType: string; key: string; content: Record<string, unknown> }> = [];
	const lines = rawBody.split(/\r?\n/);
	let i = 0;
	let freeTextLines: string[] = [];

	function pushComponent(componentType: string, content: Record<string, unknown>): void {
		components.push({ componentType, key: `${componentType}-${components.length}`, content });
	}

	function flushFreeText(): void {
		if (freeTextLines.length === 0) return;
		// Blank line = new paragraph; consecutive non-blank lines = one paragraph (markdown-style)
		const groups: string[][] = [];
		let current: string[] = [];
		for (const line of freeTextLines) {
			if (line.trim() === "") {
				if (current.length) {
					groups.push(current);
					current = [];
				}
			} else {
				current.push(line);
			}
		}
		if (current.length) groups.push(current);
		const paragraphs = groups.map((g) => g.join("\n").trim()).filter(Boolean);
		freeTextLines = [];
		if (paragraphs.length === 0) return;
		pushComponent("TextBlock", { paragraphs });
	}

	while (i < lines.length) {
		const line = lines[i];
		const trimmed = (line ?? "").trim();
		// ## Section first (so ## is not treated as # title)
		const sectionMatch = trimmed.match(SECTION_HEADING_REGEX);
		if (sectionMatch) {
			flushFreeText();
			const text = sectionMatch[1].replace(/\r?\n/g, " ").trim() || "Section";
			pushComponent("SectionHeading", { text });
			i++;
			continue;
		}

		// Single # title → PageHeader (anywhere in doc, not only at start)
		const h1Match = trimmed.match(H1_REGEX);
		if (h1Match) {
			const raw = (h1Match[1] ?? "").trim();
			if (raw !== "/") {
				flushFreeText();
				pushComponent("PageHeader", { title: raw });
			}
			i++;
			continue;
		}

		// Fenced block: ```ArticleHero ... ```
		if (FENCED_ARTICLE_HERO_OPEN_REGEX.test(trimmed)) {
			flushFreeText();
			const blockLines: string[] = [];
			i++;
			while (i < lines.length && !FENCED_BLOCK_CLOSE_REGEX.test((lines[i] ?? "").trim())) {
				blockLines.push(lines[i]);
				i++;
			}
			if (i < lines.length) i++; // consume closing ```

			const yamlStr = blockLines.join("\n").trim();
			const finalContent = parseArticleHeroBlock(yamlStr);
			pushComponent("ArticleHero", finalContent);
			continue;
		}

		const blockOpenMatch = trimmed.match(BLOCK_OPEN_REGEX);
		if (blockOpenMatch) {
			flushFreeText();
			const componentType = blockOpenMatch[1];
			const blockLines: string[] = [];
			i++;
			while (i < lines.length && !BLOCK_CLOSE_REGEX.test((lines[i] ?? "").trim())) {
				blockLines.push(lines[i]);
				i++;
			}
			if (i < lines.length) i++; // consume :::

			const yamlStr = blockLines.join("\n").trim();
			if (!yamlStr) {
				pushComponent(componentType, {});
				continue;
			}

			// ArticleHero: preprocess so unquoted values with ": " (e.g. title) parse correctly
			const yamlToParse =
				componentType === "ArticleHero" ? preprocessArticleHeroYaml(yamlStr) : yamlStr;

			let parsed: unknown;
			try {
				parsed = parse(yamlToParse);
			} catch {
				pushComponent(componentType, {});
				continue;
			}

			if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
				pushComponent(componentType, {});
				continue;
			}

			const obj = parsed as Record<string, unknown>;
			const { key: _k, ...content } = obj;
			let finalContent: Record<string, unknown> = typeof content === "object" && content !== null ? (content as Record<string, unknown>) : {};
			// ArticleHero: strip shareLabel/shareOptions (renderer uses fixed "Partager" and all social)
			if (componentType === "ArticleHero") {
				finalContent = parseArticleHeroContent(finalContent);
			}
			pushComponent(componentType, finalContent);
			continue;
		}

		freeTextLines.push(line);
		i++;
	}
	flushFreeText();

	const normalized = normalizeComponents(components);
	const metadata: PageMetadata = { ...((base?.metadata as PageMetadata) ?? {}) };
	const pageTitle = frontmatterTitle || baseFallback.title || "";
	metadata.description = frontmatterDescription;
	return {
		...baseFallback,
		title: pageTitle,
		slug: slug || baseFallback.slug,
		language: language || baseFallback.language,
		metadata,
		components: normalized
	};
}
